import React from 'react';
import { useTranslation } from 'react-i18next';

import PATHS from '../../../../Routers/PATHS';
import { ctaLaptop } from '../../assets';
import { Button } from '../../../shared/ui';

/**
 * "ابدأ رحلتك التعليمية" — the closing call to action, identical on the home,
 * catalogue, contact and certificate pages.
 *
 * Geometry from Figma node 16383:21693, a 1920×648 section:
 *   panel   1488×448 at x=216, y=108 — 23.16px radius, ground #c0d3ff at 16%
 *   rings   two hairline ellipses in #8aabf8 at 50%, children of the panel and
 *           therefore clipped by it: one escaping the top-left corner, one the
 *           bottom-right corner
 *   copy    868 wide, 48px in from the panel's right edge, 87px from its top
 *   laptop  831.38×507.03 at x=47, y=41 — a *sibling* of the panel, drawn over
 *           its top-left corner and clipped by nothing: 169px past the panel's
 *           left edge and 67px above its top, landing 8px shy of its bottom
 *
 * That sibling relationship is the whole trick. The laptop is taller than the
 * panel and hangs off two of its sides, so it cannot live inside the panel — a
 * previous pass put it in a grid column there and the panel's own bounds sheared
 * 169px off the artwork. Here it is absolutely positioned against the panel and
 * escapes freely; only the rings are clipped.
 *
 * **This composition is physical, not logical, and that is deliberate.** The
 * laptop stays on the left and the copy on the right in both languages. The
 * artwork is a photographed screen with its own perspective — the lid opens
 * towards the right — so mirroring it for English put the machine on the wrong
 * side of the fold and read as a different photo. Everything that places the two
 * boxes therefore uses `left`/`right`, and only the text *inside* the copy box
 * stays logical (`text-start`), so Arabic still reads from the right and English
 * from the left within it.
 *
 * The section's `lg` side padding is 9.3% rather than a fixed 40px so the space
 * beside the panel always covers the overhang: at 1920 it resolves to 216px on
 * each side of the 1488 panel, exactly the design, and below that both the panel
 * and the space it needs shrink together, so the laptop never runs off the page.
 *
 * The laptop is anchored by its *bottom* edge rather than its top. Its height
 * scales with the panel's width while the panel's own height does not, so
 * pinning the top would open a gap under it on a narrower screen; pinning the
 * bottom keeps the corner tight and simply lets the top overhang taper off.
 *
 * Below `lg` the panel goes fluid, the laptop drops back inside it, and nothing
 * overhangs — no clipping, and the page never scrolls sideways.
 */
/**
 * The four grounds the design puts this section on — one per page, matching
 * whatever that page's last section sat on: #f8fafc on the home and certificate
 * pages, #f5f7fa on the catalogue, white on contact.
 *
 * A prop rather than a class from the caller: `bg-v2-ground-100` and
 * `bg-v2-ground-200` are the same utility, so which one won would be settled by
 * the order the stylesheet happens to put them in, not by the attribute's.
 */
const GROUNDS = {
    page: 'tw-bg-v2-ground-100',
    tinted: 'tw-bg-v2-ground-200',
    white: 'tw-bg-white',
};

const CtaBanner = ({ ground = 'tinted', className = '' }) => {
    const { t } = useTranslation();

    return (
        <section className={`tw-overflow-x-clip ${GROUNDS[ground] ?? GROUNDS.tinted} tw-px-5 tw-py-14 md:tw-px-10 md:tw-pb-[92px] md:tw-pt-[108px] lg:tw-px-[9.3%] ${className}`}>
            <div className="tw-mx-auto tw-w-full tw-max-w-[1488px]">
                <div className="tw-relative tw-rounded-v2-panel tw-bg-v2-brand-panel lg:tw-min-h-[448px]">
                    {/* Decorative rings. They get their own clipped layer rather
                        than `overflow-hidden` on the panel, because the panel has
                        to let the laptop hang past its edge — clipping the panel
                        cut the laptop off along that edge.

                        Sized in px and offset in panel units, and both are
                        ellipses in the design, not circles. Physical sides, like
                        the rest of the composition: the first ring escapes the
                        left, behind the laptop, the second the right. */}
                    <span
                        aria-hidden
                        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-v2-panel"
                    >
                        <span className="tw-absolute tw--left-[6.86%] tw--top-[91.5px] tw-h-[296.44px] tw-w-[330.67px] tw-rounded-full tw-border tw-border-v2-brand-ring/50" />
                        <span className="tw-absolute tw--right-[5.47%] tw-top-[224.4px] tw-h-[333.5px] tw-w-[372px] tw-rounded-full tw-border tw-border-v2-brand-ring/50" />
                    </span>

                    {/* 831.38/1488 of the panel wide, hung 169/1488 past its left
                        edge, 8px up from its floor. `max-w-none` unwinds the
                        stacked sizing above it. */}
                    <div className="tw-px-6 tw-pb-8 lg:tw-absolute lg:tw--left-[11.36%] lg:tw-bottom-2 lg:tw-w-[55.87%] lg:tw-p-0">
                        <img
                            src={ctaLaptop}
                            alt=""
                            aria-hidden
                            loading="lazy"
                            width={1247}
                            height={760}
                            className="tw-mx-auto tw-w-full tw-max-w-[560px] tw-object-contain lg:tw-max-w-none"
                        />
                    </div>

                    {/* 868/1488 of the panel wide, 48/1488 in from its right edge.
                        `ml-auto` is what pushes it there — the box is in normal
                        flow, so the margin has to do the work the laptop's
                        `absolute` does, and `relative` keeps it painted on top.

                        The design lets this box overlap the laptop's last 6%,
                        and in Arabic that costs nothing: the text is aligned to
                        the right, so the overlapping strip only ever holds the
                        spent end of a line. In English every line *begins*
                        there, straight across the keyboard — so `ltr:w-[52.26%]`
                        pulls the box's left edge back to 44.51%, exactly where
                        the laptop's own box ends. The transparent margin baked
                        into the artwork supplies the gutter from there. */}
                    <div className="tw-relative tw-flex tw-flex-col tw-gap-6 tw-p-8 tw-text-start md:tw-p-12 lg:tw-ml-auto lg:tw-mr-[3.23%] lg:tw-w-[58.33%] lg:ltr:tw-w-[52.26%] lg:tw-gap-9 lg:tw-p-0 lg:tw-py-[87px]">
                        {/* No `text-balance` here: the design breaks the title
                            before "Learning-Go", which is where it falls
                            naturally in an 868px box. Balancing it pulls the
                            break a word earlier and stops matching.

                            The brand is held together by a word joiner (U+2060)
                            after the hyphen, in both translations. Without it a
                            browser reads "Learning-Go" as two breakable pieces
                            and strands "Learning-" alone at the end of the
                            line, hyphen and all. A joiner is a format
                            character rather than a glyph, so it costs nothing
                            to render and no font has to carry it — which a
                            non-breaking hyphen, missing from Tajawal, would. */}
                        <h2 className="tw-text-3xl tw-font-bold tw-leading-[1.2] tw-tracking-[-1.22px] tw-text-v2-text-heading md:tw-text-[48.6px]">
                            {t('v2.cta.title')}
                        </h2>

                        <p className="tw-text-v2-base tw-leading-[1.75] tw-text-v2-text-body md:tw-text-[18.5px]">
                            {t('v2.cta.lead')}
                        </p>

                        {/* Both languages park the pair on the right. In Arabic
                            that is the start edge and the design already puts
                            them there; `ltr:justify-end` gives English the same
                            edge, so the buttons land under the end of the
                            paragraph rather than adrift at the box's far side. */}
                        <div className="tw-flex tw-flex-wrap tw-gap-[9px] lg:ltr:tw-justify-end">
                            <Button to={PATHS.LANDING_CERTIFICATIONS.URL}>
                                {t('v2.cta.explore')}
                            </Button>
                            <Button to={PATHS.LANDING_CONTACTUS.URL} variant="dark">
                                {t('v2.cta.contact')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CtaBanner;
